import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiChevronDown, FiChevronUp, FiPlus, FiSave, FiSearch, FiTrash2, FiUser, FiUsers } from 'react-icons/fi';
import { API_URLS, getTeamImageUrl } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { unwrapUser, useAdminAccess, filterChampionshipGroups } from '../utilities/adminAccess';
import { useAuth } from '../utilities/auth';
import { adminTeamsQuery, champFields, extractList } from '../utilities/tournamentsApi';
import {
  ADMIN_ROLE_MIN,
  assignmentLabel,
  extractUser,
  filterAssignableUserRoles,
  formatUserInfoFields,
  getUserPhotoUrl,
  hasRootAssignment,
  isAdminRole,
  isCaptainRole,
  ROLE_ROOT,
  roleAssignmentKey,
  roleLabel,
  serializeRoleAssignments,
  userDisplayName,
} from '../utilities/usersApi';
import { extractPlayersPage, getPlayerPhotoUrl, playerCardPath, playerDisplayName } from '../utilities/playersApi';
import ReorderableTable from '../components/ReorderableTable';
import StadiumCrumbs from '../components/StadiumCrumbs';
import CustomModal from '../components/CustomModal';
import defaultUserImage from '../images/defaultAvatar.png';
import defaultTeamLogo from '../images/defaultTeamLogo.png';
import '../styles/createMatchModal.css';
import '../styles/stadiumsManagement.css';
import '../styles/disqualificationsManagement.css';
import '../styles/usersManagement.css';

const emptyUserForm = {
  name: '',
  family: '',
  email: '',
  phone: '',
  password: '',
  confirmPassword: '',
};

const phoneDigits = (value) => String(value || '').replace(/\D/g, '');

const CHAMPS_COLLAPSE_MQ = '(max-width: 1099px)';

const isCompactLayout = () => (
  typeof window !== 'undefined'
  && window.matchMedia
  && window.matchMedia(CHAMPS_COLLAPSE_MQ).matches
);

const teamFromPicker = (team, champId, champName) => {
  const id = Number(team.id || team.team_id);
  if (!Number.isFinite(id) || id <= 0) return null;
  const championshipId = Number(
    team.championship_id || team.championat_id || team.champ_id || champId
  );
  return {
    id,
    name: team.name || team.team_name || `#${id}`,
    championship_id: Number.isFinite(championshipId) && championshipId > 0 ? championshipId : null,
    championship_name: team.championship_name || team.championat_name || champName || null,
    image: team.image || '',
  };
};

export default function ManageUser() {
  const { userId } = useParams();
  const isNew = !userId || userId === 'new';
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useI18n();
  const { userData } = useAuth();
  const { groups, championships, champId } = useChampionships();
  const { canWrite, assignableRoles, userData: accessUser } = useAdminAccess('users');
  const assignableRoleOptions = useMemo(
    () => filterAssignableUserRoles(assignableRoles),
    [assignableRoles]
  );
  const champGroups = useMemo(
    () => filterChampionshipGroups(groups, accessUser || userData),
    [groups, accessUser, userData]
  );

  const [user, setUser] = useState(null);
  const [rolesScoped, setRolesScoped] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [form, setForm] = useState(emptyUserForm);
  const [errors, setErrors] = useState({});
  const [assignments, setAssignments] = useState([]);
  const [originalAssignments, setOriginalAssignments] = useState([]);
  const [draftRole, setDraftRole] = useState(String(ADMIN_ROLE_MIN));
  const [draftChampId, setDraftChampId] = useState('');
  const [draftTeam, setDraftTeam] = useState(null);
  const [assignmentError, setAssignmentError] = useState('');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [assignmentsOpen, setAssignmentsOpen] = useState(() => !isCompactLayout());
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const [pickerChampId, setPickerChampId] = useState('');
  const [pickerPlayers, setPickerPlayers] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerError, setPickerError] = useState('');
  const [linking, setLinking] = useState(false);
  const [unlinkPlayer, setUnlinkPlayer] = useState(null);
  const [unlinking, setUnlinking] = useState(false);
  const [teamPickerOpen, setTeamPickerOpen] = useState(false);
  const [teamPickerQuery, setTeamPickerQuery] = useState('');
  const [teamPickerChampId, setTeamPickerChampId] = useState('');
  const [teamPickerTeams, setTeamPickerTeams] = useState([]);
  const [teamPickerLoading, setTeamPickerLoading] = useState(false);
  const [teamPickerError, setTeamPickerError] = useState('');
  const pickerTimer = useRef(null);
  const pickerRequestRef = useRef(0);
  const teamPickerTimer = useRef(null);
  const teamPickerRequestRef = useRef(0);

  const currentUserId = Number(unwrapUser(userData).id || unwrapUser(userData).user_id);
  const isSelf = Number(userId) === currentUserId;
  const lockRoot = isSelf && hasRootAssignment(originalAssignments);
  const draftRoleId = Number(draftRole);
  const draftIsCaptain = isCaptainRole(draftRoleId);

  useEffect(() => {
    if (isNew) {
      setUser(null);
      setRolesScoped(false);
      setForbidden(false);
      setForm(emptyUserForm);
      setErrors({});
      setAssignments([]);
      setOriginalAssignments([]);
      const firstRole = assignableRoleOptions[0]?.id || ADMIN_ROLE_MIN;
      setDraftRole(String(firstRole));
      setDraftChampId(champId ? String(champId) : '');
      setDraftTeam(null);
      setAssignmentError('');
      setMessage({ text: '', type: '' });
      setLoading(false);
      return;
    }
    loadUser();
  }, [userId]);

  useEffect(() => {
    if (!assignableRoleOptions.some((role) => Number(role.id) === Number(draftRole))) {
      const first = assignableRoleOptions[0]?.id;
      if (first) setDraftRole(String(first));
    }
  }, [assignableRoleOptions]);

  useEffect(() => {
    if (!draftChampId && champId) setDraftChampId(String(champId));
  }, [champId]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(CHAMPS_COLLAPSE_MQ);
    const sync = () => setAssignmentsOpen(!mq.matches);
    sync();
    if (mq.addEventListener) mq.addEventListener('change', sync);
    else mq.addListener(sync);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', sync);
      else mq.removeListener(sync);
    };
  }, []);

  const applyUser = (data) => {
    const nextAssignments = Array.isArray(data.roles) ? data.roles : [];
    setUser(data);
    setRolesScoped(Boolean(data.roles_scoped));
    setForbidden(false);
    setAssignments(nextAssignments);
    setOriginalAssignments(nextAssignments);
    setAssignmentError('');
  };

  const loadUser = async () => {
    setLoading(true);
    setMessage({ text: '', type: '' });
    setForbidden(false);
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminUser, { id: Number(userId) });
      applyUser(extractUser(response.data));
    } catch (error) {
      setUser(null);
      setRolesScoped(false);
      const status = error?.response?.status;
      const text = getApiErrorMessage(error, t('usrLoadError'));
      if (status === 403) {
        setForbidden(true);
        setMessage({ text: text || t('usrForbidden'), type: 'error' });
      } else {
        setMessage({ text, type: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  const linkedPlayers = user?.players || [];
  const linkedPlayerIds = new Set(linkedPlayers.map((item) => Number(item.id)));
  const editableAssignments = useMemo(
    () => assignments.filter((item) => isAdminRole(item.role) || isCaptainRole(item.role)),
    [assignments]
  );

  const champById = useMemo(() => {
    const map = new Map();
    championships.forEach((item) => {
      if (item?.id) map.set(Number(item.id), item);
    });
    assignments.forEach((item) => {
      if (item?.championship_id && !map.has(Number(item.championship_id))) {
        map.set(Number(item.championship_id), {
          id: item.championship_id,
          name: item.championship_name || `#${item.championship_id}`,
        });
      }
    });
    return map;
  }, [championships, assignments]);

  const extraChampionships = useMemo(() => {
    const known = new Set(championships.map((item) => Number(item.id)));
    const extras = [];
    champById.forEach((item, id) => {
      if (!known.has(id)) extras.push(item);
    });
    return extras;
  }, [championships, champById]);

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const handleDraftRoleChange = (value) => {
    setDraftRole(value);
    setAssignmentError('');
    if (!isCaptainRole(Number(value))) setDraftTeam(null);
  };

  const handleDraftChampChange = (value) => {
    setDraftChampId(value);
    setAssignmentError('');
    if (draftTeam && draftTeam.championship_id && Number(draftTeam.championship_id) !== Number(value)) {
      setDraftTeam(null);
    }
  };

  const canRemoveAssignment = (item) => {
    if (!canWrite) return false;
    if (!lockRoot || Number(item.role) !== ROLE_ROOT) return true;
    return assignments.filter((assignment) => Number(assignment.role) === ROLE_ROOT).length > 1;
  };

  const validateAssignments = (list) => {
    if (lockRoot && !hasRootAssignment(list)) {
      setAssignmentError(t('usrRootLocked'));
      return false;
    }
    const incomplete = (list || []).find((item) => (
      // root глобальный — без championship_id; admin 5–6/8–13 требуют чемпионат
      (isAdminRole(item.role) && Number(item.role) !== ROLE_ROOT && !item.championship_id)
      || (isCaptainRole(item.role) && !item.team_id)
    ));
    if (incomplete) {
      setAssignmentError(isCaptainRole(incomplete.role) ? t('usrTeamRequired') : t('usrChampRequired'));
      return false;
    }
    setAssignmentError('');
    return true;
  };

  /** Существующий юзер: сразу changeAdminUser. Новый: только локальный стейт до создания. */
  const persistAssignments = async (nextList) => {
    if (!validateAssignments(nextList)) return false;
    setAssignments(nextList);
    if (isNew) return true;

    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const response = await authorizedPost(dispatch, API_URLS.changeAdminUser, {
        id: Number(userId),
        roles: serializeRoleAssignments(nextList),
      });
      applyUser(extractUser(response.data));
      setMessage({ text: t('stSaved'), type: 'success' });
      return true;
    } catch (error) {
      const apiMsg = getApiErrorMessage(error, '');
      const champDenied = /чемпионат|championship|champ/i.test(apiMsg)
        && /доступ|access|forbidden|прав/i.test(apiMsg);
      setMessage({
        text: champDenied ? t('usrChampNoAccess') : (apiMsg || t('stSaveError')),
        type: 'error',
      });
      setAssignments(originalAssignments);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveAssignment = async (item) => {
    if (!canRemoveAssignment(item) || saving) return;
    const key = roleAssignmentKey(item);
    await persistAssignments(assignments.filter((assignment) => roleAssignmentKey(assignment) !== key));
  };

  const handleAddAssignment = async () => {
    if (!canWrite || !draftRoleId || saving) return;
    let next = null;
    if (draftIsCaptain) {
      if (!draftTeam?.id) {
        setAssignmentError(t('usrTeamRequired'));
        return;
      }
      const captainChampId = Number(
        draftTeam.championship_id || draftChampId || 0
      );
      if (!Number.isFinite(captainChampId) || captainChampId <= 0) {
        // команда без чемпионата / чемпионат не в доступе текущего админа
        setAssignmentError(t('usrChampNoAccess'));
        return;
      }
      if (
        champById.size
        && !champById.has(captainChampId)
      ) {
        setAssignmentError(t('usrChampNoAccess'));
        return;
      }
      next = {
        role: draftRoleId,
        team_id: Number(draftTeam.id),
        team_name: draftTeam.name || null,
        championship_id: captainChampId,
        championship_name: draftTeam.championship_name
          || champById.get(captainChampId)?.name
          || null,
      };
    } else {
      const championshipId = Number(draftChampId);
      if (!Number.isFinite(championshipId) || championshipId <= 0) {
        setAssignmentError(t('usrChampRequired'));
        return;
      }
      const champ = champById.get(championshipId);
      next = {
        role: draftRoleId,
        championship_id: championshipId,
        championship_name: champ?.name || null,
      };
    }
    if (!next) return;
    if (assignments.some((item) => roleAssignmentKey(item) === roleAssignmentKey(next))) {
      setAssignmentError(t('usrAssignmentExists'));
      return;
    }
    const ok = await persistAssignments(assignments.concat(next));
    if (ok) {
      setDraftTeam(null);
      setAssignmentError('');
    }
  };

  const validateCreate = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = t('usrNameRequired');
    if (!form.family.trim()) nextErrors.family = t('usrFamilyRequired');
    const password = form.password.trim();
    const confirm = form.confirmPassword.trim();
    const phone = form.phone.trim();
    if (!password && !phone) nextErrors.password = t('usrPasswordOrPhone');
    if (password && password.length < 8) nextErrors.password = t('usrPasswordMin');
    if (password && password !== confirm) nextErrors.confirmPassword = t('usrPasswordMismatch');
    if (phone && phoneDigits(phone).length < 10) nextErrors.phone = t('usrPhoneInvalid');
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateCreate()) return;
    if (!validateAssignments(assignments)) return;
    setSaving(true);
    setMessage({ text: '', type: '' });
    const payload = {
      name: form.name.trim(),
      family: form.family.trim(),
    };
    if (form.email.trim()) payload.email = form.email.trim();
    if (form.phone.trim()) payload.phone = form.phone.trim();
    if (form.password.trim()) {
      payload.password = form.password.trim();
      if (form.confirmPassword.trim()) payload.confirmPassword = form.confirmPassword.trim();
    }
    const rolesToSave = serializeRoleAssignments(assignments);
    if (rolesToSave.length) payload.roles = rolesToSave;
    try {
      const response = await authorizedPost(dispatch, API_URLS.addAdminUser, payload);
      const created = extractUser(response.data);
      if (created.id) history.replace(`/users/${created.id}`);
      else history.push('/users');
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stSaveError')), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const closePicker = () => {
    setPickerOpen(false);
    setPickerQuery('');
    setPickerPlayers([]);
    setPickerError('');
    setPickerLoading(false);
  };

  const openPicker = () => {
    setPickerChampId(champId ? String(champId) : (championships[0]?.id ? String(championships[0].id) : ''));
    setPickerQuery('');
    setPickerPlayers([]);
    setPickerError('');
    setPickerOpen(true);
  };

  const closeTeamPicker = () => {
    setTeamPickerOpen(false);
    setTeamPickerQuery('');
    setTeamPickerTeams([]);
    setTeamPickerError('');
    setTeamPickerLoading(false);
  };

  const openTeamPicker = () => {
    setTeamPickerChampId(draftChampId || (champId ? String(champId) : ''));
    setTeamPickerQuery('');
    setTeamPickerTeams([]);
    setTeamPickerError('');
    setTeamPickerOpen(true);
  };

  const loadPickerPlayers = async (search, nextChampId) => {
    const requestId = ++pickerRequestRef.current;
    const champ = Number(nextChampId);
    if (!Number.isFinite(champ) || champ <= 0) {
      setPickerPlayers([]);
      setPickerLoading(false);
      return;
    }
    setPickerLoading(true);
    setPickerError('');
    try {
      const body = {
        ...champFields(champ),
        limit: 20,
        offset: 0,
      };
      if (search && search.trim()) body.search = search.trim();
      const response = await authorizedPost(dispatch, API_URLS.getAdminPlayers, body);
      if (requestId !== pickerRequestRef.current) return;
      setPickerPlayers(extractPlayersPage(response.data).items);
    } catch (error) {
      if (requestId !== pickerRequestRef.current) return;
      setPickerPlayers([]);
      setPickerError(getApiErrorMessage(error, t('pmSearchError')));
    } finally {
      if (requestId === pickerRequestRef.current) setPickerLoading(false);
    }
  };

  const loadPickerTeams = async (search, nextChampId) => {
    const requestId = ++teamPickerRequestRef.current;
    setTeamPickerLoading(true);
    setTeamPickerError('');
    try {
      const response = await authorizedPost(
        dispatch,
        API_URLS.getCatalogTeams,
        adminTeamsQuery(nextChampId, {
          search,
          limit: 20,
          offset: 0,
        })
      );
      if (requestId !== teamPickerRequestRef.current) return;
      setTeamPickerTeams(extractList(response.data));
    } catch (error) {
      if (requestId !== teamPickerRequestRef.current) return;
      setTeamPickerTeams([]);
      setTeamPickerError(getApiErrorMessage(error, t('trTeamsNotFound')));
    } finally {
      if (requestId === teamPickerRequestRef.current) setTeamPickerLoading(false);
    }
  };

  useEffect(() => {
    if (!pickerOpen) return undefined;
    if (pickerTimer.current) clearTimeout(pickerTimer.current);
    const delay = pickerQuery ? 350 : 0;
    pickerTimer.current = setTimeout(() => {
      loadPickerPlayers(pickerQuery, pickerChampId);
    }, delay);
    return () => {
      if (pickerTimer.current) clearTimeout(pickerTimer.current);
    };
  }, [pickerOpen, pickerQuery, pickerChampId]);

  useEffect(() => {
    if (!teamPickerOpen) return undefined;
    if (teamPickerTimer.current) clearTimeout(teamPickerTimer.current);
    const delay = teamPickerQuery ? 350 : 0;
    teamPickerTimer.current = setTimeout(() => {
      loadPickerTeams(teamPickerQuery, teamPickerChampId);
    }, delay);
    return () => {
      if (teamPickerTimer.current) clearTimeout(teamPickerTimer.current);
    };
  }, [teamPickerOpen, teamPickerQuery, teamPickerChampId]);

  const handleLinkPlayer = async (player) => {
    const playerId = Number(player.id || player.player_id);
    if (!playerId || linking) return;
    setLinking(true);
    setMessage({ text: '', type: '' });
    try {
      const response = await authorizedPost(dispatch, API_URLS.linkAdminUserPlayer, {
        id: Number(userId),
        player_id: playerId,
      });
      applyUser(extractUser(response.data));
      closePicker();
      setMessage({ text: t('stSaved'), type: 'success' });
    } catch (error) {
      setPickerError(getApiErrorMessage(error, t('usrLinkError')));
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkPlayer = async () => {
    const playerId = Number(unlinkPlayer?.id);
    if (!playerId) return;
    setUnlinking(true);
    setMessage({ text: '', type: '' });
    try {
      const response = await authorizedPost(dispatch, API_URLS.unlinkAdminUserPlayer, {
        id: Number(userId),
        player_id: playerId,
      });
      applyUser(extractUser(response.data));
      setUnlinkPlayer(null);
      setMessage({ text: t('stSaved'), type: 'success' });
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('usrUnlinkError')), type: 'error' });
      setUnlinkPlayer(null);
    } finally {
      setUnlinking(false);
    }
  };

  const handlePickTeam = (team) => {
    const picked = teamFromPicker(
      team,
      teamPickerChampId,
      champById.get(Number(teamPickerChampId))?.name
    );
    if (!picked) return;
    setDraftTeam(picked);
    if (picked.championship_id) setDraftChampId(String(picked.championship_id));
    closeTeamPicker();
    setAssignmentError('');
  };

  const infoFields = useMemo(() => formatUserInfoFields(user || {}, t), [user, t]);
  const photoUrl = getUserPhotoUrl(user?.image);

  const playerColumns = [
    {
      key: 'name',
      label: t('pmFio'),
      className: 'st-cell-name',
      render: (item) => (
        <span className="usr-player-cell">
          <img
            src={getPlayerPhotoUrl(item.image) || defaultUserImage}
            alt=""
            onError={(event) => { event.currentTarget.src = defaultUserImage; }}
          />
          <span>{playerDisplayName(item) || '—'}</span>
        </span>
      ),
    },
    {
      key: 'id',
      label: t('usrId'),
      className: 'usr-cell-id',
      render: (item) => item.id || '—',
    },
    {
      key: 'champ',
      label: t('championship'),
      className: 'usr-cell-champs',
      render: (item) => item.championship_name || item.championat_name || '—',
    },
    ...(canWrite ? [{
      key: 'actions',
      label: t('pdActions'),
      className: 'st-cell-actions',
      render: (item) => (
        <button
          type="button"
          className="st-icon-btn danger"
          title={t('usrUnlinkPlayer')}
          onClick={(event) => {
            event.stopPropagation();
            setUnlinkPlayer(item);
          }}
        >
          <FiTrash2 />
        </button>
      ),
    }] : []),
  ];

  const title = isNew ? t('usrNewUser') : (user?.login || userDisplayName(user) || t('usrCardTitle'));

  if (!isNew && loading) {
    return (
      <div className="layoutWrapper st-layout">
        <div className="matchCenterWrapper st-page">
          <div className="matchCenterHeader">
            <div className="st-header">
              <StadiumCrumbs
                items={[
                  { label: t('navUsers'), to: '/users' },
                  { label: t('usrCardTitle') },
                ]}
              />
            </div>
          </div>
          <div className="st-loading">
            <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
            <p>{t('usrLoadingCard')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs
              items={[
                { label: t('navUsers'), to: '/users' },
                { label: title },
              ]}
            />
          </div>
        </div>

        {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

        <div className="st-scroll">
          <div className="st-content usr-split">
            <section className="st-block">
              <h2 className="st-block-header">{isNew ? t('usrNewUser') : t('usrFields')}</h2>
              <div>
                {isNew ? (
                  <div className="st-form-grid">
                    <div className="st-field st-field-wide">
                      <p className="st-hint">{t('usrPasswordOrPhone')}</p>
                    </div>
                    <div className="st-field">
                      <label>{t('usrEmail')}</label>
                      <input
                        className="st-input"
                        value={form.email}
                        onChange={(event) => updateForm('email', event.target.value)}
                      />
                    </div>
                    <div className="st-field">
                      <label>{t('usrFamily')} <span className="st-required">*</span></label>
                      <input
                        className={`st-input ${errors.family ? 'error' : ''}`}
                        value={form.family}
                        onChange={(event) => updateForm('family', event.target.value)}
                      />
                      {errors.family ? <span className="st-field-error">{errors.family}</span> : null}
                    </div>
                    <div className="st-field">
                      <label>{t('usrName')} <span className="st-required">*</span></label>
                      <input
                        className={`st-input ${errors.name ? 'error' : ''}`}
                        value={form.name}
                        onChange={(event) => updateForm('name', event.target.value)}
                      />
                      {errors.name ? <span className="st-field-error">{errors.name}</span> : null}
                    </div>
                    <div className="st-field">
                      <label>{t('usrPhone')}</label>
                      <input
                        className={`st-input ${errors.phone ? 'error' : ''}`}
                        value={form.phone}
                        placeholder={t('usrPhonePh')}
                        onChange={(event) => updateForm('phone', event.target.value)}
                      />
                      {errors.phone ? <span className="st-field-error">{errors.phone}</span> : null}
                    </div>
                    <div className="st-field">
                      <label>{t('usrPassword')}</label>
                      <input
                        type="password"
                        className={`st-input ${errors.password ? 'error' : ''}`}
                        value={form.password}
                        placeholder={t('usrPasswordPh')}
                        onChange={(event) => updateForm('password', event.target.value)}
                      />
                      {errors.password ? <span className="st-field-error">{errors.password}</span> : null}
                    </div>
                    <div className="st-field">
                      <label>{t('usrConfirmPassword')}</label>
                      <input
                        type="password"
                        className={`st-input ${errors.confirmPassword ? 'error' : ''}`}
                        value={form.confirmPassword}
                        onChange={(event) => updateForm('confirmPassword', event.target.value)}
                      />
                      {errors.confirmPassword ? <span className="st-field-error">{errors.confirmPassword}</span> : null}
                    </div>
                  </div>
                ) : !user ? (
                  <div className="st-empty"><p>{t('usrNotFound')}</p></div>
                ) : (
                  <>
                    <div className="usr-card-head">
                      <img
                        src={photoUrl || defaultUserImage}
                        alt=""
                        className="usr-avatar"
                        onError={(event) => { event.currentTarget.src = defaultUserImage; }}
                      />
                      <div>
                        <div className="usr-card-login">{user.login || '—'}</div>
                        <div className="usr-card-name">{userDisplayName(user) || '—'}</div>
                        <div className="usr-chips">
                          {(user.roles || []).map((item) => (
                            <span
                              key={roleAssignmentKey(item)}
                              className={`usr-chip ${Number(item.role) === Number(user.role) ? 'is-main' : ''}`}
                            >
                              {assignmentLabel(t, item)}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="usr-info-grid">
                      {infoFields.map((field) => (
                        <div key={field.key} className="usr-info-item">
                          <span>{field.label}</span>
                          <strong>{field.value}</strong>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </section>

            <section className={`st-block usr-champs-panel ${assignmentsOpen ? 'is-open' : ''}`}>
              <h2 className="st-block-header usr-champs-title">{t('usrEditRoles')}</h2>
              <button
                type="button"
                className="usr-champs-toggle"
                onClick={() => setAssignmentsOpen((open) => !open)}
              >
                <span>
                  {t('usrEditRoles')}
                  <span className="usr-champs-count">{t('usrAssignmentsSelected', { count: editableAssignments.length })}</span>
                </span>
                {assignmentsOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>
              {!assignmentsOpen && editableAssignments.length ? (
                <div className="usr-chips usr-champs-preview">
                  {editableAssignments.map((item) => (
                    <span key={roleAssignmentKey(item)} className="usr-chip is-main">
                      {assignmentLabel(t, item)}
                    </span>
                  ))}
                </div>
              ) : null}
              <div className="usr-champ-body">
                {editableAssignments.length ? (
                  <div className="usr-assign-list">
                    {editableAssignments.map((item) => {
                      const removable = canRemoveAssignment(item);
                      return (
                        <div key={roleAssignmentKey(item)} className="usr-assign-row">
                          <div className="usr-assign-row-body">
                            <div className="usr-assign-role">{roleLabel(t, item.role)}</div>
                            <div className="usr-assign-meta">
                              {[item.team_name, item.championship_name].filter(Boolean).join(' · ') || '—'}
                            </div>
                          </div>
                          {canWrite && !forbidden ? (
                            <button
                              type="button"
                              className="st-icon-btn danger"
                              title={removable ? t('usrRemoveAssignment') : t('usrRootLocked')}
                              disabled={!removable || saving}
                              onClick={() => handleRemoveAssignment(item)}
                            >
                              <FiTrash2 />
                            </button>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="st-hint">{t('usrNoAssignments')}</p>
                )}

                {canWrite && !forbidden ? (
                  <div className={`usr-assign-add ${draftIsCaptain ? 'is-captain' : ''}`}>
                    <div className="st-field">
                      <label>{t('usrSelectRole')}</label>
                      <select
                        className="st-select"
                        value={draftRole}
                        onChange={(event) => handleDraftRoleChange(event.target.value)}
                      >
                        {assignableRoleOptions.map((role) => (
                          <option key={role.id} value={role.id}>{t(role.labelKey)}</option>
                        ))}
                      </select>
                    </div>
                    {!draftIsCaptain ? (
                      <div className="st-field">
                        <label>{t('usrSelectChamp')}</label>
                        <select
                          className="st-select"
                          value={draftChampId}
                          onChange={(event) => handleDraftChampChange(event.target.value)}
                        >
                          <option value="">{draftIsCaptain ? t('usrAllChamps') : t('usrSelectChamp')}</option>
                          {champGroups.map((city) => (
                            <optgroup key={city.city_id || city.city} label={city.city || city.city_ru || '—'}>
                              {(city.championships || []).map((champ) => (
                                <option key={champ.id} value={champ.id}>{champ.name}</option>
                              ))}
                            </optgroup>
                          ))}
                          {extraChampionships.map((champ) => (
                            <option key={champ.id} value={champ.id}>{champ.name || `#${champ.id}`}</option>
                          ))}
                        </select>
                      </div>
                    ) : null}
                    {draftIsCaptain ? (
                      <div className="st-field">
                        <label>{t('usrSelectTeam')}</label>
                        <button
                          type="button"
                          className="st-select usr-team-pick"
                          onClick={openTeamPicker}
                        >
                          {draftTeam?.name || t('usrPickTeam')}
                        </button>
                      </div>
                    ) : null}
                    <button
                      type="button"
                      className="st-btn st-btn-secondary usr-assign-add-btn"
                      disabled={saving}
                      onClick={handleAddAssignment}
                    >
                      <FiPlus /> {saving ? t('trLoading') : t('usrAddAssignment')}
                    </button>
                  </div>
                ) : null}
                {assignmentError ? <p className="st-field-error">{assignmentError}</p> : null}
                {lockRoot ? <p className="st-hint">{t('usrRootLocked')}</p> : null}
                {rolesScoped ? <p className="st-hint">{t('usrRolesScopedHint')}</p> : null}
                {!isNew ? <p className="st-hint">{t('usrRolesAutosaveHint')}</p> : null}
                <p className="st-hint">{t('usrAutoRolesHint')}</p>
              </div>
            </section>

            {isNew ? null : (
            <section className="st-block usr-players-panel">
              <h2 className="st-block-header">{t('usrPlayers')}</h2>
              <div>
                <ReorderableTable
                  tableClassName={`st-table-user-players${canWrite ? ' has-actions' : ''}`}
                  columns={playerColumns}
                  items={linkedPlayers}
                  getRowKey={(item) => item.id}
                  reorderMode={false}
                  getRowHref={(item) => (item.id ? playerCardPath(item.id) : null)}
                  empty={<div className="st-empty"><p>{t('usrNoPlayers')}</p></div>}
                />
                {canWrite && !forbidden ? (
                  <div className="usr-players-actions">
                    <button
                      type="button"
                      className="st-btn st-btn-secondary"
                      disabled={linking}
                      onClick={openPicker}
                    >
                      <FiPlus /> {t('usrLinkPlayer')}
                    </button>
                  </div>
                ) : null}
              </div>
            </section>
            )}
          </div>
        </div>

        {canWrite && !forbidden && isNew ? (
          <div className="st-actions">
            <button
              type="button"
              className="st-btn st-btn-primary"
              disabled={loading || saving}
              onClick={handleCreate}
            >
              <FiSave /> {saving ? t('trLoading') : t('usrCreate')}
            </button>
          </div>
        ) : null}
      </div>

      <CustomModal
        styles={{ width: 480, height: 'auto', paddingLeft: 24, paddingRight: 24, paddingTop: 20, paddingBottom: 20 }}
        visible={pickerOpen}
        closeModal={() => !linking && closePicker()}
      >
        <div className="dq-picker-modal">
          <div className="customModalHeader">{t('usrPickPlayerTitle')}</div>
          <select
            className="st-select usr-picker-champ"
            value={pickerChampId}
            onChange={(event) => setPickerChampId(event.target.value)}
          >
            {!pickerChampId ? <option value="">{t('usrAllChamps')}</option> : null}
            {championships.map((champ) => (
              <option key={champ.id} value={champ.id}>{champ.name}</option>
            ))}
          </select>
          <div className="st-search">
            <FiSearch className="st-search-icon" />
            <input
              type="text"
              className="st-search-input"
              placeholder={t('usrPlayerSearchPh')}
              value={pickerQuery}
              autoFocus
              onChange={(event) => setPickerQuery(event.target.value)}
            />
            {pickerQuery ? (
              <button type="button" className="st-search-clear" onClick={() => setPickerQuery('')}>
                ×
              </button>
            ) : null}
          </div>
          {pickerError ? <div className="st-field-error">{pickerError}</div> : null}
          {pickerLoading || linking ? (
            <div className="dq-picker-loading">
              <Loader type="Oval" color="#3ad1a0" height={36} width={36} />
              <p>{t('ttLoadingPlayers')}</p>
            </div>
          ) : (
            <div className="dq-picker-list">
              {pickerPlayers.length ? pickerPlayers.map((player) => {
                const playerId = Number(player.id || player.player_id);
                const already = linkedPlayerIds.has(playerId);
                return (
                  <button
                    type="button"
                    key={playerId}
                    className="dq-picker-item"
                    disabled={already || linking}
                    onClick={() => handleLinkPlayer(player)}
                  >
                    <img
                      src={getPlayerPhotoUrl(player.image) || defaultUserImage}
                      alt=""
                      onError={(event) => { event.currentTarget.src = defaultUserImage; }}
                    />
                    <span>
                      <strong>{playerDisplayName(player) || `#${playerId}`}</strong>
                      <em>
                        {already
                          ? t('usrAlreadyLinked')
                          : (player.championship_name || player.championat_name || t('ttNoTeam'))}
                      </em>
                    </span>
                  </button>
                );
              }) : (
                <div className="dq-picker-empty">
                  <FiUser /> {t('pmNoResults')}
                </div>
              )}
            </div>
          )}
        </div>
      </CustomModal>

      <CustomModal
        styles={{ width: 480, height: 'auto', paddingLeft: 24, paddingRight: 24, paddingTop: 20, paddingBottom: 20 }}
        visible={teamPickerOpen}
        closeModal={closeTeamPicker}
      >
        <div className="dq-picker-modal">
          <div className="customModalHeader">{t('usrPickTeamTitle')}</div>
          <select
            className="st-select usr-picker-champ"
            value={teamPickerChampId}
            onChange={(event) => setTeamPickerChampId(event.target.value)}
          >
            <option value="">{t('usrAllChamps')}</option>
            {championships.map((champ) => (
              <option key={champ.id} value={champ.id}>{champ.name}</option>
            ))}
          </select>
          <div className="st-search">
            <FiSearch className="st-search-icon" />
            <input
              type="text"
              className="st-search-input"
              placeholder={t('usrTeamSearchPh')}
              value={teamPickerQuery}
              autoFocus
              onChange={(event) => setTeamPickerQuery(event.target.value)}
            />
            {teamPickerQuery ? (
              <button type="button" className="st-search-clear" onClick={() => setTeamPickerQuery('')}>
                ×
              </button>
            ) : null}
          </div>
          {teamPickerError ? <div className="st-field-error">{teamPickerError}</div> : null}
          {teamPickerLoading ? (
            <div className="dq-picker-loading">
              <Loader type="Oval" color="#3ad1a0" height={36} width={36} />
              <p>{t('trTeamSearching')}</p>
            </div>
          ) : (
            <div className="dq-picker-list">
              {teamPickerTeams.length ? teamPickerTeams.map((team) => {
                const teamId = Number(team.id || team.team_id);
                const taken = assignments.some((item) => (
                  Number(item.role) === draftRoleId && Number(item.team_id) === teamId
                ));
                return (
                  <button
                    type="button"
                    key={teamId}
                    className="dq-picker-item"
                    disabled={taken}
                    onClick={() => handlePickTeam(team)}
                  >
                    <img
                      src={getTeamImageUrl(team.image) || defaultTeamLogo}
                      alt=""
                      onError={(event) => { event.currentTarget.src = defaultTeamLogo; }}
                    />
                    <span>
                      <strong>{team.name || team.team_name || `#${teamId}`}</strong>
                      <em>
                        {taken
                          ? t('usrAlreadyAssignedTeam')
                          : (team.championship_name || team.championat_name || champById.get(Number(teamPickerChampId))?.name || t('usrSelectChamp'))}
                      </em>
                    </span>
                  </button>
                );
              }) : (
                <div className="dq-picker-empty">
                  <FiUsers /> {t('trTeamsNotFound')}
                </div>
              )}
            </div>
          )}
        </div>
      </CustomModal>

      <CustomModal
        styles={{ width: 420, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
        visible={Boolean(unlinkPlayer)}
        closeModal={() => !unlinking && setUnlinkPlayer(null)}
      >
        <div className="st-delete-modal">
          <div className="customModalHeader">{t('usrUnlinkPlayer')}</div>
          <p>{t('usrUnlinkPlayerText', { name: playerDisplayName(unlinkPlayer) || t('usrPlayers') })}</p>
          <div className="st-delete-actions">
            <button
              type="button"
              className="btn-modal btn-secondary-modal"
              disabled={unlinking}
              onClick={() => setUnlinkPlayer(null)}
            >
              {t('cancel')}
            </button>
            <button
              type="button"
              className="btn-modal btn-danger-modal"
              disabled={unlinking}
              onClick={handleUnlinkPlayer}
            >
              {unlinking ? t('trLoading') : t('usrUnlinkPlayer')}
            </button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}
